import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    WebsiteUrl: state.components.contactUsForm.fieldValues["WebsiteUrl"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface WebsiteUrlStyles {
    websiteUrlTextField?: TextFieldPresentationProps;
}

export const WebsiteUrlStyles: WebsiteUrlStyles = {
    websiteUrlTextField: {
        css: css`
            border-radius: 8px !important;
        `,
        cssOverrides: {
            formField: css`
                margin-bottom: 20px;
            `,

            formInputWrapper: css`
                margin-top: 4px !important;
            `,
        },
    },
};

const styles = WebsiteUrlStyles;
const websiteRegexp =
    /^((https?|ftp|smtp):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/;
const WebsiteUrl: React.FC<Props> = ({ fields, WebsiteUrl, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [WebsiteUrlErrorMessage, setWebsiteUrlErrorMessage] = React.useState("");

    React.useEffect(() => {
        contactFormContext.validators.WebsiteUrl = validateWebsiteUrl;
    }, [WebsiteUrl]);

    React.useEffect(() => {
        contactFormContext.validators.WebsiteUrl = validateWebsiteUrl;
    }, [isRequired]);

    const validateWebsiteUrl = () => {
        const errorMessage =
            isRequired && !WebsiteUrl
                ? (siteMessage("Field_Required", label) as string)
                : !WebsiteUrl || websiteRegexp.test(WebsiteUrl)
                ? ""
                : (siteMessage("Field_Invalid", label) as string);
        setWebsiteUrlErrorMessage(errorMessage as string);
        return !errorMessage;
    };

    const WebsiteUrlChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "WebsiteUrl", value: event.target.value });
    };

    const WebsiteUrlBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        validateWebsiteUrl();
    }

    return (
        <TextField
            {...styles.websiteUrlTextField}
            label={label}
            required={isRequired}
            value={WebsiteUrl || ""}
            error={WebsiteUrlErrorMessage}
            onBlur={WebsiteUrlBlurHandler}
            onChange={WebsiteUrlChangeHandler}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(WebsiteUrl),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        displayName: "WebsiteUrl",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: false,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
